import { useEffect, useMemo, useState } from "react";
import { useApi } from "../contexts/ApiProvider";
import {
  defaultAchievements,
  defaultGlobalContent,
  defaultPages,
} from "./defaultContent";

function deepMerge(defaultValue, cmsValue) {
  if (Array.isArray(defaultValue)) {
    return Array.isArray(cmsValue) ? cmsValue : defaultValue;
  }

  if (
    defaultValue &&
    typeof defaultValue === "object" &&
    cmsValue &&
    typeof cmsValue === "object" &&
    !Array.isArray(cmsValue)
  ) {
    return Object.keys({ ...defaultValue, ...cmsValue }).reduce((acc, key) => {
      acc[key] = deepMerge(defaultValue[key], cmsValue[key]);
      return acc;
    }, {});
  }

  return cmsValue === undefined || cmsValue === null || cmsValue === ""
    ? defaultValue
    : cmsValue;
}

function applySeo(seo) {
  if (!seo) return;
  if (seo.title) document.title = seo.title;

  if (seo.description) {
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", seo.description);
  }
}

function isPreviewMode() {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.get("cmsPreview") === "1";
}

function normalizeSlug(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function useCmsPage(slug) {
  const api = useApi();
  const defaults = useMemo(() => defaultPages[slug] || defaultPages.home, [slug]);
  const [content, setContent] = useState(defaults);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      const previewMode = isPreviewMode();
      const response = await api.get(
        previewMode ? `/api/cms/admin/pages/${slug}` : `/api/cms/pages/${slug}`
      );
      if (!isMounted) return;

      if (response.success && response.page) {
        const merged = deepMerge(defaults, {
          title: response.page.title,
          seo: response.page.seo,
          sections: response.page.sections,
        });
        setContent({ ...merged, blocks: response.page.blocks || [] });
        applySeo(merged.seo);
      } else {
        setContent(defaults);
        applySeo(defaults.seo);
      }
      setIsLoading(false);
    })();

    return () => {
      isMounted = false;
    };
  }, [api, defaults, slug]);

  return { content, isLoading };
}

export function useCmsGlobal() {
  const api = useApi();
  const [globalContent, setGlobalContent] = useState(defaultGlobalContent);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      const response = await api.get("/api/cms/global");
      if (!isMounted) return;

      if (response.success && response.global) {
        setGlobalContent(deepMerge(defaultGlobalContent, response.global.data));
      } else {
        setGlobalContent(defaultGlobalContent);
      }
      setIsLoading(false);
    })();

    return () => {
      isMounted = false;
    };
  }, [api]);

  return { globalContent, isLoading };
}

export function useCmsAchievements() {
  const api = useApi();
  const [achievements, setAchievements] = useState(defaultAchievements);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      const response = await api.get("/api/cms/achievements");
      if (!isMounted) return;

      if (response.success && response.achievements?.length) {
        setAchievements(response.achievements);
      } else {
        setAchievements(defaultAchievements);
      }
      setIsLoading(false);
    })();

    return () => {
      isMounted = false;
    };
  }, [api]);

  return { achievements, isLoading };
}

export function useCmsAchievement(slug) {
  const api = useApi();
  const fallback = useMemo(
    () =>
      defaultAchievements.find(
        (achievement) => normalizeSlug(achievement.slug || achievement.title) === normalizeSlug(slug)
      ) || null,
    [slug]
  );
  const [achievement, setAchievement] = useState(fallback);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      const response = await api.get(`/api/cms/achievements/${slug}`);
      if (!isMounted) return;

      if (response.success && response.achievement) {
        setAchievement(response.achievement);
      } else {
        setAchievement(fallback);
      }
      setIsLoading(false);
    })();

    return () => {
      isMounted = false;
    };
  }, [api, fallback, slug]);

  return { achievement, isLoading };
}
